/**
 * **Vorn und hinten** auf der Pinnwand (CONTEXT.md).
 *
 * Neuere Elemente liegen über älteren — das bleibt die Regel, solange niemand
 * etwas anderes will. „Ganz nach vorn" und „Ganz nach hinten" schreiben dafür
 * `zOrder`; ohne Wert zählt das Alter (`createdAt` in ms). Bestehende Elemente
 * behalten so ihre Reihenfolge, ohne dass eine Zeile umgeschrieben wird.
 *
 * **Zwei Ebenen:** Bereiche liegen immer unter allen Zetteln (ein Bereich fasst
 * Zettel zusammen, er deckt sie nicht zu). Vorn und hinten gilt deshalb nur
 * innerhalb der eigenen Ebene.
 *
 * Bewusst ohne React-Bezug, wie die übrigen `utils/`.
 */

import type { CanvasNote } from '@/src/renderer/lib/db/models'
import type { HistoryEntry } from './canvasHistory'

type Stackable = Pick<CanvasNote, 'id' | 'kind' | 'zOrder' | 'createdAt'>

export type ZOrderDirection = 'front' | 'back'

/** Wo ein Element in seiner Ebene liegt — größer heißt weiter vorn. */
export function zKeyOf(note: Stackable): number {
  return note.zOrder ?? note.createdAt.getTime()
}

const layerOf = (note: Stackable) => (note.kind === 'area' ? 0 : 1)

/**
 * Von hinten nach vorn: erst die Bereiche, dann die Zettel, darin nach
 * `zKeyOf`. Bei Gleichstand die Kennung — sonst entschiede die Reihenfolge der
 * Abfrage, und zwei Geräte zeigten dieselbe Pinnwand verschieden überdeckt.
 */
export function compareZ(a: Stackable, b: Stackable): number {
  return layerOf(a) - layerOf(b)
    || zKeyOf(a) - zKeyOf(b)
    || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
}

/**
 * Die Änderungen für „Ganz nach vorn" bzw. „Ganz nach hinten" — als Einträge
 * für den Verlauf (`before`/`after` je Zeile), damit ⌘Z sie zurücknimmt.
 *
 * - **Nach vorn:** größter Wert der übrigen Ebene + 1, + 2 …
 * - **Nach hinten:** kleinster Wert − n … − 1.
 *
 * Mehrere behalten ihre Reihenfolge untereinander. Liegt die Auswahl einer
 * Ebene schon ganz vorn bzw. hinten, entsteht für sie nichts — ein leeres
 * Ergebnis heißt: Der Eintrag ist grau.
 */
export function planZOrder(
  notes: readonly Stackable[],
  ids: Iterable<string>,
  direction: ZOrderDirection,
): HistoryEntry[] {
  const chosen = new Set(ids)
  const entries: HistoryEntry[] = []

  for (const layer of [0, 1]) {
    const stack = notes.filter(n => layerOf(n) === layer).sort(compareZ)
    const moving = stack.filter(n => chosen.has(n.id))
    const others = stack.filter(n => !chosen.has(n.id))
    if (moving.length === 0 || others.length === 0) continue

    // Schon am Ziel, wenn die Gewählten genau die obersten bzw. untersten sind.
    const edge = direction === 'front' ? stack.slice(-moving.length) : stack.slice(0, moving.length)
    if (edge.every(n => chosen.has(n.id))) continue

    const base = direction === 'front'
      ? Math.max(...others.map(zKeyOf)) + 1
      : Math.min(...others.map(zKeyOf)) - moving.length
    moving.forEach((note, i) => {
      entries.push({ id: note.id, before: { zOrder: note.zOrder ?? null }, after: { zOrder: base + i } })
    })
  }

  return entries
}
